import * as React from "react";

import { cn } from "./shadcn/utils";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./shadcn/ui/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "./shadcn/ui/drawer";
import { ScrollArea } from "./shadcn/ui/scroll-area";

const desktop = "(min-width: 768px)";

const SimpleKitModal = ({ className, children, ...props }: any) => {
  const isDesktop = useMediaQuery(desktop);
  const KitModal = isDesktop ? Dialog : Drawer;

  return (
    <KitModal
      {...props}
      className={cn(
        "web3kit-transition-transform web3kit-duration-300",
        className
      )}
    >
      {children}
    </KitModal>
  );
};

const SimpleKitModalTrigger = ({ className, children, ...props }: any) => {
  const isDesktop = useMediaQuery(desktop);
  const ModalTrigger = isDesktop ? DialogTrigger : DrawerTrigger;

  return (
    <ModalTrigger className={className} {...props}>
      {children}
    </ModalTrigger>
  );
};

const SimpleKitModalClose = ({ className, children, ...props }: any) => {
  const isDesktop = useMediaQuery(desktop);
  const ModalClose = isDesktop ? DialogClose : DrawerClose;

  return (
    <ModalClose
      className={cn("hover:web3kit-bg-accent web3kit-p-2", className)}
      {...props}
    >
      {children}
    </ModalClose>
  );
};

const SimpleKitModalContent = ({ className, children, ...props }: any) => {
  const isDesktop = useMediaQuery(desktop);
  const ModalContent = isDesktop ? DialogContent : DrawerContent;

  return (
    <ModalContent
      className={cn(
        "web3kit-bg-card web3kit-text-card-foreground !web3kit-border-0 !web3kit-rounded-3xl md:web3kit-max-w-[380px] [&>button]:web3kit-right-[26px] [&>button]:web3kit-top-[26px] web3kit-transition-transform web3kit-duration-300 web3kit-z-[60]",
        className
      )}
      onOpenAutoFocus={(e) => e.preventDefault()}
      {...props}
    >
      {children}
    </ModalContent>
  );
};

const SimpleKitModalDescription = ({ className, children, ...props }: any) => {
  const isDesktop = useMediaQuery(desktop);
  const ModalDescription = isDesktop ? DialogDescription : DrawerDescription;

  return (
    <ModalDescription className={className} {...props}>
      {children}
    </ModalDescription>
  );
};

const SimpleKitModalHeader = ({ className, children, ...props }: any) => {
  const isDesktop = useMediaQuery(desktop);
  const ModalHeader = isDesktop ? DialogHeader : DrawerHeader;

  return (
    <ModalHeader
      className={cn(
        "web3kit-space-y-0 web3kit-pb-6 md:web3kit-pb-3",
        className
      )}
      {...props}
    >
      {children}
    </ModalHeader>
  );
};

const SimpleKitModalTitle = ({ className, children, ...props }: any) => {
  const isDesktop = useMediaQuery(desktop);
  const ModalTitle = isDesktop ? DialogTitle : DrawerTitle;
  return (
    <ModalTitle
      className={cn("web3kit-text-center web3kit-py-2", className)}
      {...props}
    >
      {children}
    </ModalTitle>
  );
};

const SimpleKitModalBody = ({ className, children, ...props }: any) => {
  return (
    <ScrollArea
      className={cn(
        "web3kit-h-[260px] web3kit-max-h-[350px] web3kit-px-6 md:web3kit-h-full md:web3kit-min-h-[260px] md:web3kit-px-0",
        className
      )}
      {...props}
    >
      <div className="web3kit-space-y-2">{children}</div>
    </ScrollArea>
  );
};

const SimpleKitModalFooter = ({ className, children, ...props }: any) => {
  const isDesktop = useMediaQuery(desktop);
  const ModalFooter = isDesktop ? DialogFooter : DrawerFooter;

  return (
    <ModalFooter
      className={cn("web3kit-py-3.5 md:web3kit-py-0", className)}
      {...props}
    >
      <div className="web3kit-flex web3kit-flex-col web3kit-gap-y-4 web3kit-w-full">
        {children}
      </div>
    </ModalFooter>
  );
};

export {
  SimpleKitModal,
  SimpleKitModalTrigger,
  SimpleKitModalClose,
  SimpleKitModalContent,
  SimpleKitModalDescription,
  SimpleKitModalHeader,
  SimpleKitModalTitle,
  SimpleKitModalBody,
  SimpleKitModalFooter,
};

function useMediaQuery(query: string) {
  const [matches, setMatches] = React.useState(false);

  React.useEffect(() => {
    const mediaQueryList = matchMedia(query);

    setMatches(mediaQueryList.matches);

    const updateMatch = (event: MediaQueryListEvent) =>
      setMatches(event.matches);

    mediaQueryList.addEventListener("change", updateMatch);

    return () => mediaQueryList.removeEventListener("change", updateMatch);
  }, [query]);

  return matches;
}
